import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Send, Trash2 } from 'lucide-react';
import { rupee } from './labPrint';
import { errMsg } from './labApi';
import { periodRange } from './LabSummary';
import { KIND_LABEL, StatusBadge, periodText, SettlementMeta } from './settlementShared';

const defaultRange = (kind, today) => {
  if (kind === 'weekly_local') {
    const r = periodRange('week', today);
    return r.end > today ? { ...r, end: today } : r;
  }
  const d = new Date(`${today}T00:00:00Z`);
  const prev = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1)).toISOString().slice(0, 10);
  return periodRange('month', prev);
};

export const LabSettlements = ({ api, today }) => {
  const [kind, setKind] = useState('weekly_local');
  const [range, setRange] = useState(defaultRange('weekly_local', today));
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [list, setList] = useState([]);

  const load = useCallback(() => api.get('/settlements').then(setList).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);

  const pickKind = (k) => { setKind(k); setRange(defaultRange(k, today)); };
  const submit = async () => {
    if (!(parseFloat(amount) > 0)) return toast.error('Enter the amount');
    try {
      await api.post('/settlements', { kind, ...range, amount: parseFloat(amount), note });
      toast.success('Settlement query sent to doctor');
      setAmount(''); setNote(''); load();
    } catch (e) { toast.error(errMsg(e, 'Could not raise query')); }
  };
  const withdraw = async (s) => {
    if (!window.confirm('Withdraw this pending query?')) return;
    try { await api.del(`/settlements/${s.id}`); load(); } catch (e) { toast.error(errMsg(e, 'Could not withdraw')); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="lab-settlements">
      <h3 className="font-bold">Raise Settlement Query</h3>
      <div className="inline-flex rounded-lg border overflow-hidden">
        {Object.entries(KIND_LABEL).map(([k, l]) => (
          <button key={k} type="button" onClick={() => pickKind(k)} data-testid={`lab-settle-kind-${k}`}
            className={`px-3 py-1.5 text-xs font-bold ${kind === k ? 'bg-teal-700 text-white' : 'bg-white text-slate-600'}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <label className="text-xs text-slate-500 space-y-0.5">From<Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-9" data-testid="lab-settle-start" /></label>
        <label className="text-xs text-slate-500 space-y-0.5">To<Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-9" data-testid="lab-settle-end" /></label>
        <label className="text-xs text-slate-500 space-y-0.5">Amount ₹<Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))} className="h-9" data-testid="lab-settle-amount" /></label>
        <label className="text-xs text-slate-500 space-y-0.5">Note / Invoice no.<Input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} className="h-9" data-testid="lab-settle-note" /></label>
      </div>
      <Button onClick={submit} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="lab-settle-submit"><Send className="h-4 w-4 mr-1" /> Send to Doctor</Button>

      <div className="space-y-2 pt-2 border-t">
        <h4 className="font-semibold text-sm">My Queries ({list.length})</h4>
        {list.length === 0 && <p className="text-sm text-slate-400">No queries yet</p>}
        {list.map((s) => (
          <div key={s.id} className="border rounded-xl p-3 bg-white" data-testid={`lab-settle-${s.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div>
                <p className="font-bold text-sm">{KIND_LABEL[s.kind]} <span className="font-normal text-slate-500">• {periodText(s)}</span></p>
                <SettlementMeta s={s} />
              </div>
              <div className="text-right space-y-1">
                <p className="font-black">{rupee(s.amount)}</p>
                <StatusBadge status={s.status} testId={`lab-settle-status-${s.id}`} />
                {s.status === 'pending' && <button onClick={() => withdraw(s)} className="block ml-auto text-red-500" data-testid={`lab-settle-withdraw-${s.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
